import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Anchor, Users, Waves, Check, Instagram as InstagramIcon } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { WaButton } from "./SiteLayout";

const Img = ({ src, alt = "", className = "" }) => <img src={fileUrl(src)} alt={alt} loading="lazy" className={`object-cover ${className}`} />;
const Eyebrow = ({ children }) => <p className="uppercase tracking-[0.25em] text-xs font-semibold text-ocean">{children}</p>;
const Para = ({ text, className = "" }) => (text || "").split("\n").filter(Boolean).map((t, i) => <p key={i} className={className}>{t}</p>);

const Hero = ({ s, wa }) => (
  <section className="px-3 sm:px-5 pt-3" data-testid={`section-${s.id}`}>
    <div className="relative overflow-hidden rounded-[2rem] min-h-[78vh] flex items-end">
      <Img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full hero-drift" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
      <div className="relative max-w-7xl w-full mx-auto px-6 lg:px-12 pb-14 lg:pb-20 text-white">
        {s.eyebrow && <p className="rise uppercase tracking-[0.3em] text-xs sm:text-sm font-semibold text-white/85">{s.eyebrow}</p>}
        <h1 className={`rise rise-2 font-display text-4xl sm:text-5xl lg:text-7xl mt-3 max-w-4xl break-words ${s.title?.startsWith("#") ? "notranslate" : ""}`} translate={s.title?.startsWith("#") ? "no" : undefined}>{s.title}</h1>
        <div className="rise rise-3 mt-5 max-w-xl text-base sm:text-lg text-white/90 space-y-1"><Para text={s.body} /></div>
        <WaButton href={wa} label="Plan with a local" testid="hero-whatsapp-btn" className="rise rise-3 mt-8" />
      </div>
    </div>
  </section>
);

const Story = ({ s }) => (
  <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-32 grid lg:grid-cols-12 gap-12 items-center" data-testid={`section-${s.id}`}>
    <div className="lg:col-span-6 space-y-6">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">{s.title}</h2>
      {s.subtitle && <p className="text-lg text-slate-700 leading-relaxed">{s.subtitle}</p>}
      <div className="space-y-4 text-slate-600 leading-relaxed"><Para text={s.body} /></div>
    </div>
    {s.image && <div className="lg:col-span-5 lg:col-start-8"><Img src={s.image} className="w-full aspect-[4/5] rounded-[2rem]" /></div>}
  </section>
);

const Cards = ({ s }) => (
  <section className="bg-sand py-20 lg:py-28" data-testid={`section-${s.id}`}>
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-12">{s.title}</h2>
      <div className="grid md:grid-cols-3 gap-6">
        {s.items.map((it, i) => (
          <Link key={it.id} to={it.link || "/"} data-testid={`journey-card-${i}`}
            className={`group bg-white rounded-[1.75rem] overflow-hidden flex flex-col hover:-translate-y-1 transition-transform duration-300 ${i === 1 ? "md:translate-y-10" : ""}`}>
            <div className="overflow-hidden"><Img src={it.image} alt={it.title} className="w-full aspect-[4/3] group-hover:scale-105 transition-transform duration-700" /></div>
            <div className="p-7 flex-1 flex flex-col gap-3">
              <h3 className="font-admin font-bold uppercase tracking-wide text-lg flex items-center justify-between">{it.title}<ArrowUpRight className="w-5 h-5 text-ocean" /></h3>
              <p className="text-slate-600 text-[15px] leading-relaxed">{it.body}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const ICONS = [Compass, Anchor, Users, Waves];
const Values = ({ s }) => (
  <section className="max-w-7xl mx-auto px-5 lg:px-10 py-24 lg:py-36" data-testid={`section-${s.id}`}>
    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-14 max-w-xl">{s.title}</h2>
    <div className="grid sm:grid-cols-2 gap-x-12 gap-y-14">
      {s.items.map((it, i) => {
        const Icon = ICONS[i % 4];
        return (
          <div key={it.id} className="flex gap-5" data-testid={`value-${i}`}>
            <div className="shrink-0 w-14 h-14 rounded-2xl bg-soft text-ocean grid place-items-center"><Icon className="w-6 h-6" /></div>
            <div className="space-y-2">
              <h3 className="font-admin font-bold text-xl">{it.title}</h3>
              {it.subtitle && <p className="font-medium text-ink">{it.subtitle}</p>}
              <p className="text-slate-600 leading-relaxed">{it.body}</p>
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

const Gallery = ({ s }) => (
  <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24" data-testid={`section-${s.id}`}>
    {s.title && <h2 className="font-display text-3xl sm:text-4xl mb-10">{s.title}</h2>}
    <div className="columns-2 md:columns-3 gap-4 [&>*]:mb-4">
      {s.items.map((it) => <Img key={it.id} src={it.image} alt={it.title || "Gallery photo"} className="w-full rounded-2xl break-inside-avoid" />)}
    </div>
  </section>
);

const Services = ({ s }) => (
  <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12" data-testid={`section-${s.id}`}>
    <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-28 self-start">
      <Eyebrow>Day trips from Labuan Bajo</Eyebrow>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">{s.title}</h2>
      {s.image && <Img src={s.image} className="w-full aspect-[4/3] rounded-[2rem]" />}
    </div>
    <div className="lg:col-span-7 divide-y divide-border">
      {s.items.map((it, i) => (
        <div key={it.id} className="py-8 first:pt-0" data-testid={`service-${i}`}>
          <h3 className="font-admin font-bold text-2xl">{it.title}</h3>
          {it.subtitle && <p className="mt-2 text-ocean font-medium">{it.subtitle}</p>}
          <p className="mt-3 text-slate-600 leading-relaxed">{it.body}</p>
        </div>
      ))}
    </div>
  </section>
);

const Courses = ({ s }) => (
  <section className="bg-soft/60 py-20 lg:py-28" data-testid={`section-${s.id}`}>
    <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
      <Img src={s.image} className="w-full aspect-[4/3] rounded-[2rem] order-last lg:order-first" />
      <div className="space-y-6">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">{s.title}</h2>
        <p className="text-lg text-slate-700">{s.subtitle}</p>
        <ul className="grid sm:grid-cols-2 gap-3">
          {s.items.map((it, i) => (
            <li key={it.id} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 font-medium" data-testid={`course-${i}`}>
              <Check className="w-4 h-4 text-ocean" />{it.title}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const Packages = ({ s, wa }) => (
  <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28" data-testid={`section-${s.id}`}>
    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-12">{s.title}</h2>
    <div className="grid md:grid-cols-3 gap-6">
      {s.items.map((it, i) => (
        <div key={it.id} className="rounded-[1.75rem] border border-border overflow-hidden flex flex-col" data-testid={`package-${i}`}>
          {it.image && <Img src={it.image} className="w-full aspect-[16/10]" />}
          <div className="p-7 flex-1 flex flex-col gap-3">
            <h3 className="font-display text-3xl">{it.title}</h3>
            <p className="font-medium text-ocean">{it.subtitle}</p>
            <p className="text-slate-600 leading-relaxed flex-1">{it.body}</p>
            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid={`package-enquire-${i}`} className="mt-3 inline-flex items-center gap-1 font-semibold text-oceandeep hover:underline">Ask about dates <ArrowUpRight className="w-4 h-4" /></a>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Cta = ({ s, wa }) => (
  <section className="px-3 sm:px-5 py-6" data-testid={`section-${s.id}`}>
    <div className="relative overflow-hidden rounded-[2rem] bg-oceandeep">
      {s.image && <Img src={s.image} className="absolute inset-0 w-full h-full opacity-30" />}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 text-white grid lg:grid-cols-2 gap-8 items-end">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">{s.title}</h2>
        <div className="space-y-6">
          <p className="text-lg text-white/90">{s.body}</p>
          <WaButton href={wa} label="Dive with Indo Aura" testid={`cta-whatsapp-${s.id}`} />
        </div>
      </div>
    </div>
  </section>
);

const Contact = ({ s, settings }) => (
  <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center" data-testid={`section-${s.id}`}>
    <div className="space-y-6">
      <Eyebrow>{s.subtitle}</Eyebrow>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl">{s.title}</h1>
      <p className="text-lg text-slate-600">{s.body}</p>
      <WaButton href={settings.whatsapp_link} testid="contact-whatsapp-btn" />
      <div className="grid sm:grid-cols-2 gap-4 pt-4">
        {[["Email", settings.email, `mailto:${settings.email}`], ["Phone / WhatsApp", settings.phone, settings.whatsapp_link],
          ["Instagram", settings.instagram_handle, settings.instagram], ["Facebook", settings.facebook_name, settings.facebook]].map(([l, v, h]) => (
          <a key={l} href={h} target="_blank" rel="noopener noreferrer" data-testid={`contact-${l.split(" ")[0].toLowerCase()}`} className="rounded-2xl bg-sand p-5 hover:bg-soft transition-colors duration-200">
            <p className="text-xs uppercase tracking-widest text-slate-500">{l}</p>
            <p className="font-semibold mt-1 break-all">{v}</p>
          </a>
        ))}
      </div>
    </div>
    {s.image && <Img src={s.image} className="w-full aspect-[4/5] rounded-[2rem]" />}
  </section>
);

const Instagram = ({ s, settings }) => (
  <section className="py-16 lg:py-24 overflow-hidden" data-testid={`section-${s.id}`}>
    <div className="max-w-7xl mx-auto px-5 lg:px-10 flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <Eyebrow>On Instagram</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl mt-2">{s.title}</h2>
      </div>
      <a href={settings.instagram} target="_blank" rel="noopener noreferrer" data-testid="insta-follow-btn"
        className="notranslate inline-flex items-center gap-2 rounded-full border border-ink px-5 py-3 font-semibold hover:bg-ink hover:text-white transition-colors duration-200">
        <InstagramIcon className="w-5 h-5" />Follow {settings.instagram_handle}
      </a>
    </div>
    <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-5 scroll-px-5 lg:px-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))] lg:scroll-px-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))] pb-2 [scrollbar-width:none]" data-testid="insta-strip">
      {s.items.map((it, i) => (
        <a key={it.id} href={it.link || settings.instagram} target="_blank" rel="noopener noreferrer" data-testid={`insta-photo-${i}`}
          className="group relative shrink-0 snap-start w-56 sm:w-64 aspect-square rounded-2xl overflow-hidden">
          <Img src={it.image} alt={it.title || "Dive photo"} className="w-full h-full group-hover:scale-105 transition-transform duration-500" />
          <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-colors duration-300 grid place-items-center">
            <InstagramIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </span>
        </a>
      ))}
    </div>
  </section>
);

const MAP = { hero: Hero, story: Story, cards: Cards, values: Values, gallery: Gallery, instagram: Instagram, services: Services, courses: Courses, packages: Packages, cta: Cta, contact: Contact };

export const Section = ({ s, settings }) => {
  const C = MAP[s.type] || Story;
  return <C s={s} wa={settings.whatsapp_link} settings={settings} />;
};
